import { useMemo, useState } from "react";
import { api, ApiError, type Policy } from "../api.ts";
import { keplrGetPubkey } from "../keplr.ts";

// --- action vocabulary (plain-language labels instead of raw method ids) ---
type ActionKey = "gov.vote" | "bank.send" | "staking.delegate" | "distribution.withdrawRewards";
const ACTIONS: ActionKey[] = ["gov.vote", "bank.send", "staking.delegate", "distribution.withdrawRewards"];
const ACTION_META: Record<ActionKey, { label: string; icon: string; verb: string; hasAmount: boolean; targetLabel?: string; isVote?: boolean }> = {
  "gov.vote": { label: "Vote in governance", icon: "🗳️", verb: "vote on governance proposals", hasAmount: false, isVote: true },
  "bank.send": { label: "Send tokens", icon: "💸", verb: "send tokens", hasAmount: true, targetLabel: "Allowed recipient addresses" },
  "staking.delegate": { label: "Delegate (stake)", icon: "🥩", verb: "delegate (stake) tokens", hasAmount: true, targetLabel: "Allowed validators" },
  "distribution.withdrawRewards": { label: "Withdraw rewards", icon: "🎁", verb: "withdraw staking rewards", hasAmount: false },
};

// networks Praxis can operate on (chip label -> real chain id used in the policy).
// Praxis is chain-agnostic (any Cosmos chain via CosmJS); this is the curated picker.
const REG = "https://cdn.jsdelivr.net/gh/cosmos/chain-registry@master";
interface Net {
  id: string;
  label: string;
  logo: string;
  color: string;
}
const NETWORKS: Net[] = [
  { id: "provider", label: "Cosmos Hub testnet", logo: `${REG}/testnets/cosmoshubtestnet/images/atom.svg`, color: "#5064fb" },
  { id: "cosmoshub-4", label: "Cosmos Hub", logo: `${REG}/cosmoshub/images/atom.svg`, color: "#5064fb" },
  { id: "osmosis-1", label: "Osmosis", logo: `${REG}/osmosis/images/osmo.svg`, color: "#760fb1" },
  { id: "neutron-1", label: "Neutron", logo: `${REG}/neutron/images/neutron.svg`, color: "#000000" },
  { id: "juno-1", label: "Juno", logo: `${REG}/juno/images/juno.svg`, color: "#f0827d" },
  { id: "stargaze-1", label: "Stargaze", logo: `${REG}/stargaze/images/stars.svg`, color: "#db2777" },
  { id: "celestia", label: "Celestia", logo: `${REG}/celestia/images/celestia.svg`, color: "#7b2bf9" },
  { id: "dydx-mainnet-1", label: "dYdX", logo: `${REG}/dydx/images/dydx.svg`, color: "#6966ff" },
  { id: "injective-1", label: "Injective", logo: `${REG}/injective/images/inj.svg`, color: "#00a3ff" },
  { id: "akashnet-2", label: "Akash", logo: `${REG}/akash/images/akt.svg`, color: "#e2484d" },
  { id: "stride-1", label: "Stride", logo: `${REG}/stride/images/strd.svg`, color: "#e91179" },
  { id: "kaiyo-1", label: "Kujira", logo: `${REG}/kujira/images/kuji.svg`, color: "#e53935" },
  { id: "phoenix-1", label: "Terra", logo: `${REG}/terra2/images/luna.svg`, color: "#0e3b8c" },
  { id: "pacific-1", label: "Sei", logo: `${REG}/sei/images/sei.svg`, color: "#9f1d20" },
];

const UATOM_PER_ATOM = 1_000_000;

function list(s: string): string[] {
  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

/** Human ATOM string -> integer uatom string (6 decimals), or null if blank/invalid. */
function atomToUatom(atom: string): string | null {
  const t = atom.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * UATOM_PER_ATOM).toString();
}

// per-capability form state (one enabled capability = one rule in the policy)
interface CapState {
  enabled: boolean;
  maxAtom: string;
  targets: string;
  proposals: string;
  requireApprovals: string;
  windowAtom: string;
  windowHours: string;
  hourStart: string;
  hourEnd: string;
  notBefore: string;
  notAfter: string;
  groupChain: string;
  groupId: string;
}
const DEFAULT_CAP: CapState = {
  enabled: false,
  maxAtom: "",
  targets: "",
  proposals: "",
  requireApprovals: "0",
  windowAtom: "",
  windowHours: "",
  hourStart: "",
  hourEnd: "",
  notBefore: "",
  notAfter: "",
  groupChain: "",
  groupId: "",
};
type Caps = Record<ActionKey, CapState>;
function freshCaps(): Caps {
  return {
    "gov.vote": { ...DEFAULT_CAP },
    "bank.send": { ...DEFAULT_CAP },
    "staking.delegate": { ...DEFAULT_CAP },
    "distribution.withdrawRewards": { ...DEFAULT_CAP },
  };
}

// one-click starting points. ADDITIVE: enabling a template merges its capabilities
// onto whatever is already on (it never wipes the others).
interface QuickTemplate {
  id: string;
  icon: string;
  name: string;
  blurb: string;
  caps: Partial<Record<ActionKey, Partial<CapState>>>;
}
const TEMPLATES: QuickTemplate[] = [
  { id: "vote", icon: "🗳️", name: "Governance voting", blurb: "Vote on proposals.", caps: { "gov.vote": { proposals: "*" } } },
  { id: "send", icon: "💸", name: "Capped payouts", blurb: "Send up to a limit, to a whitelist.", caps: { "bank.send": { maxAtom: "0.1" } } },
  { id: "stake", icon: "🥩", name: "Staking", blurb: "Delegate to validators, with a cap.", caps: { "staking.delegate": { maxAtom: "10" } } },
  { id: "rewards", icon: "🎁", name: "Collect rewards", blurb: "Withdraw staking rewards.", caps: { "distribution.withdrawRewards": {} } },
  { id: "approval", icon: "✋", name: "Payout w/ approval", blurb: "Send, but a human must approve each one.", caps: { "bank.send": { maxAtom: "5", requireApprovals: "1" } } },
  {
    id: "treasury",
    icon: "🏦",
    name: "DAO treasury manager",
    blurb: "Vote + stake + capped payouts (needs approval) - several rules in one policy.",
    caps: { "gov.vote": { proposals: "*" }, "staking.delegate": { maxAtom: "100" }, "bank.send": { maxAtom: "5", requireApprovals: "1" } },
  },
  {
    id: "payroll",
    icon: "📅",
    name: "DAO monthly payroll + staking",
    blurb: "Monthly staking budget + payouts to a member whitelist, approved by the DAO group. Fill in members + group.",
    caps: {
      "staking.delegate": { maxAtom: "5000", windowAtom: "20000", windowHours: "720" },
      "bank.send": { maxAtom: "2000", windowAtom: "10000", windowHours: "720", requireApprovals: "1" },
    },
  },
];

// build the constraints object for one enabled capability
function constraintsFor(a: ActionKey, c: CapState): Record<string, unknown> {
  const k: Record<string, unknown> = {};
  const m = ACTION_META[a];
  if (m.hasAmount) {
    const ua = atomToUatom(c.maxAtom);
    if (ua) k.maxAmount = { denom: "uatom", amount: ua };
    if (c.targets.trim()) k.allowedRecipients = list(c.targets);
    const wua = atomToUatom(c.windowAtom);
    const wh = Number(c.windowHours);
    if (wua && Number.isFinite(wh) && wh > 0) k.windowAmount = { denom: "uatom", amount: wua, windowSeconds: Math.round(wh * 3600) };
  }
  if (m.isVote && c.proposals.trim()) k.allowedProposals = c.proposals.trim() === "*" ? "*" : list(c.proposals).map((n) => Number(n));
  const reqN = Number(c.requireApprovals) || 0;
  if (reqN > 0) k.requireApprovals = reqN;
  if (c.hourStart.trim() && c.hourEnd.trim()) k.hoursOfDayUtc = [Number(c.hourStart), Number(c.hourEnd)];
  if (c.notBefore.trim()) k.notBefore = c.notBefore.trim();
  if (c.notAfter.trim()) k.notAfter = c.notAfter.trim();
  if (c.groupChain.trim() && c.groupId.trim()) k.approvalGroup = { chainId: c.groupChain.trim(), groupId: c.groupId.trim() };
  return k;
}

/** Chain logo from the Cosmos chain-registry, with a coloured-initial fallback if it fails to load. */
function ChainIcon({ net }: { net: Net }) {
  const [ok, setOk] = useState(true);
  if (ok) return <img className="cicon" src={net.logo} alt="" onError={() => setOk(false)} />;
  return (
    <span className="cicon fallback" style={{ background: net.color }}>
      {net.label.charAt(0).toUpperCase()}
    </span>
  );
}

// ---- "write your own" validation (live, field-level) ----
const KNOWN_ACTIONS = new Set<string>(ACTIONS);

interface Analysis {
  parsed: unknown;
  errors: string[];
  warnings: string[];
}
function validateConstraints(c: Record<string, unknown>, i: number, errors: string[], warnings: string[]) {
  const coin = (v: unknown, name: string) => {
    if (typeof v !== "object" || v === null) {
      errors.push(`rules[${i}].constraints.${name} must be { denom, amount }.`);
      return false;
    }
    const o = v as Record<string, unknown>;
    if (typeof o.denom !== "string") errors.push(`rules[${i}].${name}.denom must be a string.`);
    if (typeof o.amount !== "string" || !/^\d+$/.test(o.amount)) errors.push(`rules[${i}].${name}.amount must be an integer string in base units (e.g. "100000" uatom).`);
    return true;
  };
  if (c.maxAmount !== undefined) coin(c.maxAmount, "maxAmount");
  if (c.windowAmount !== undefined && coin(c.windowAmount, "windowAmount")) {
    if (typeof (c.windowAmount as Record<string, unknown>).windowSeconds !== "number") errors.push(`rules[${i}].windowAmount.windowSeconds must be a number (seconds).`);
  }
  if (c.allowedRecipients !== undefined && !(Array.isArray(c.allowedRecipients) && c.allowedRecipients.every((x) => typeof x === "string"))) {
    errors.push(`rules[${i}].allowedRecipients must be an array of address strings.`);
  }
  if (c.allowedProposals !== undefined && c.allowedProposals !== "*" && !(Array.isArray(c.allowedProposals) && c.allowedProposals.every((x) => typeof x === "number"))) {
    errors.push(`rules[${i}].allowedProposals must be a number[] or "*".`);
  }
  if (c.requireApprovals !== undefined && (typeof c.requireApprovals !== "number" || c.requireApprovals < 0)) errors.push(`rules[${i}].requireApprovals must be a non-negative number.`);
  if (c.approvalTtlSec !== undefined && typeof c.approvalTtlSec !== "number") errors.push(`rules[${i}].approvalTtlSec must be a number (seconds).`);
  if (c.approvalGroup !== undefined) {
    const g = c.approvalGroup as Record<string, unknown>;
    if (typeof g !== "object" || g === null || typeof g.chainId !== "string" || typeof g.groupId !== "string") errors.push(`rules[${i}].approvalGroup must be { chainId, groupId }.`);
  }
  if (c.hoursOfDayUtc !== undefined) {
    const h = c.hoursOfDayUtc;
    if (!(Array.isArray(h) && h.length === 2 && h.every((x) => typeof x === "number" && x >= 0 && x <= 24))) errors.push(`rules[${i}].hoursOfDayUtc must be [start, end] within 0..24.`);
  }
  for (const k of ["notBefore", "notAfter"] as const) {
    const v = c[k];
    if (v !== undefined && (typeof v !== "string" || Number.isNaN(Date.parse(v)))) warnings.push(`rules[${i}].${k} should be an RFC3339 timestamp (e.g. 2026-01-01T00:00:00Z).`);
  }
}
function analyzePolicyJson(text: string): Analysis {
  const errors: string[] = [];
  const warnings: string[] = [];
  const t = text.trim();
  if (!t) return { parsed: null, errors: ["Empty - write a policy, or load one by id."], warnings };
  let p: unknown;
  try {
    p = JSON.parse(t);
  } catch (e) {
    return { parsed: null, errors: ["Invalid JSON - " + (e as Error).message], warnings };
  }
  if (typeof p !== "object" || p === null || Array.isArray(p)) return { parsed: p, errors: ["Top level must be a JSON object."], warnings };
  const o = p as Record<string, unknown>;
  if (typeof o.id !== "string" || !o.id.trim()) errors.push("`id` is required (string).");
  if (typeof o.org !== "string" || !o.org.trim()) errors.push("`org` is required (string).");

  if (!Array.isArray(o.subjects)) {
    errors.push("`subjects` must be an array.");
  } else {
    if (!o.subjects.some((s) => typeof s === "object" && s !== null && (s as Record<string, unknown>).type === "operator")) {
      warnings.push('No subject with type "operator" - that is usually the bot/key that acts.');
    }
    o.subjects.forEach((s, i) => {
      if (typeof s !== "object" || s === null) {
        errors.push(`subjects[${i}] must be an object.`);
        return;
      }
      const su = s as Record<string, unknown>;
      if (typeof su.id !== "string") errors.push(`subjects[${i}].id must be a string.`);
      if (typeof su.type !== "string") errors.push(`subjects[${i}].type must be a string.`);
    });
  }

  if (!Array.isArray(o.rules)) {
    errors.push("`rules` must be an array.");
  } else {
    if (o.rules.length === 0) warnings.push("No rules - this policy denies everything by default.");
    o.rules.forEach((r, i) => {
      if (typeof r !== "object" || r === null) {
        errors.push(`rules[${i}] must be an object.`);
        return;
      }
      const ru = r as Record<string, unknown>;
      if (ru.effect !== "allow" && ru.effect !== "deny") errors.push(`rules[${i}].effect must be "allow" or "deny".`);
      if (!Array.isArray(ru.actions) || ru.actions.length === 0) errors.push(`rules[${i}].actions must be a non-empty array.`);
      else ru.actions.forEach((a) => typeof a === "string" && !KNOWN_ACTIONS.has(a) && warnings.push(`rules[${i}]: action "${a}" isn’t one the executor maps - it can be denied, but won’t execute.`));
      if (!Array.isArray(ru.chains) || ru.chains.length === 0) warnings.push(`rules[${i}] has no chains - it applies to no network.`);
      if (!Array.isArray(ru.subjects) || ru.subjects.length === 0) warnings.push(`rules[${i}] has no subjects.`);
      if (ru.constraints !== undefined) {
        if (typeof ru.constraints !== "object" || ru.constraints === null) errors.push(`rules[${i}].constraints must be an object.`);
        else validateConstraints(ru.constraints as Record<string, unknown>, i, errors, warnings);
      }
    });
  }
  return { parsed: p, errors, warnings };
}

const SCHEMA_CHEATSHEET = `policy = {
  id, org,
  subjects: [{ id, type: "operator" | "member",
               publicKey?: { scheme: "secp256k1", key: "<base64>" } }],
  rules: [{
    id, effect: "allow" | "deny",
    actions: ["bank.send" | "gov.vote" | "staking.delegate" | "distribution.withdrawRewards"],
    chains: ["cosmoshub-4", ...],
    subjects: ["svc:bot", ...],
    constraints: {
      maxAmount:    { denom: "uatom", amount: "100000" },
      windowAmount: { denom: "uatom", amount: "500000", windowSeconds: 86400 },
      allowedRecipients: ["cosmos1…"],
      allowedProposals:  [42, 77] | "*",
      requireApprovals:  2,
      approvalTtlSec:    3600,
      approvalGroup:     { chainId: "cosmoshub-4", groupId: "42" },
      hoursOfDayUtc:     [8, 18],
      notBefore: "2026-01-01T00:00:00Z",
      notAfter:  "2026-12-31T23:59:59Z"
    }
  }]
}
// deny overrides allow · anything not allowed is denied by default`;

// the most-advanced showcase policy (multi-chain fund/DAO treasury bot).
const ADVANCED_EXAMPLE = {
  id: "fund-ops",
  org: "stellar-fund",
  subjects: [
    { id: "svc:treasury-bot", type: "operator" },
    { id: "member:alice", type: "member", publicKey: { scheme: "secp256k1", key: "REPLACE_alice_base64_pubkey" } },
    { id: "member:bob", type: "member", publicKey: { scheme: "secp256k1", key: "REPLACE_bob_base64_pubkey" } },
  ],
  rules: [
    {
      id: "payroll",
      effect: "allow",
      actions: ["bank.send"],
      chains: ["cosmoshub-4", "osmosis-1"],
      subjects: ["svc:treasury-bot"],
      constraints: {
        maxAmount: { denom: "uatom", amount: "2000000000" },
        windowAmount: { denom: "uatom", amount: "10000000000", windowSeconds: 2592000 },
        allowedRecipients: ["cosmos1member01xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx", "cosmos1member02xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"],
        requireApprovals: 2,
        approvalGroup: { chainId: "cosmoshub-4", groupId: "42" },
        approvalTtlSec: 86400,
        hoursOfDayUtc: [7, 19],
        notBefore: "2026-01-01T00:00:00Z",
        notAfter: "2026-12-31T23:59:59Z",
        remotePreconditions: [
          { query: { kind: "balance", chainId: "cosmoshub-4", address: "cosmos1treasuryxxxxxxxxxxxxxxxxxxxxxxxxxxxxx", denom: "uatom" }, op: "gte", value: "50000000000" },
        ],
      },
    },
    {
      id: "monthly-staking",
      effect: "allow",
      actions: ["staking.delegate"],
      chains: ["cosmoshub-4"],
      subjects: ["svc:treasury-bot"],
      constraints: {
        maxAmount: { denom: "uatom", amount: "5000000000" },
        windowAmount: { denom: "uatom", amount: "20000000000", windowSeconds: 2592000 },
        allowedRecipients: ["cosmosvaloper1daoaxxxxxxxxxxxxxxxxxxxxxxxxxxxx", "cosmosvaloper1daobxxxxxxxxxxxxxxxxxxxxxxxxxxxx"],
      },
    },
    {
      id: "governance",
      effect: "allow",
      actions: ["gov.vote"],
      chains: ["cosmoshub-4", "osmosis-1"],
      subjects: ["svc:treasury-bot"],
      constraints: {
        allowedProposals: "*",
        remotePreconditions: [{ query: { kind: "proposalStatus", chainId: "cosmoshub-4" }, op: "in_voting_period" }],
      },
    },
    {
      id: "rewards",
      effect: "allow",
      actions: ["distribution.withdrawRewards"],
      chains: ["cosmoshub-4"],
      subjects: ["svc:treasury-bot"],
      constraints: {},
    },
    {
      id: "retire-old-bot",
      effect: "deny",
      actions: ["gov.vote", "bank.send", "staking.delegate", "distribution.withdrawRewards"],
      chains: ["cosmoshub-4", "osmosis-1"],
      subjects: ["svc:old-bot"],
      constraints: {},
    },
  ],
};

export function Policies() {
  const [mode, setMode] = useState<"guided" | "advanced">("guided");
  const [advJson, setAdvJson] = useState("");
  const [editLoadId, setEditLoadId] = useState("");
  const [editLoadMsg, setEditLoadMsg] = useState("");

  const [id, setId] = useState("p1");
  const [org, setOrg] = useState("acme");
  const [subjectId, setSubjectId] = useState("svc:bot");

  const [networks, setNetworks] = useState<string[]>(["provider"]);
  const [customNet, setCustomNet] = useState("");

  const [paused, setPaused] = useState(false);
  const [caps, setCaps] = useState<Caps>(freshCaps);

  // optional human approver (m-of-n via Keplr)
  const [approverId, setApproverId] = useState("");
  const [approverKey, setApproverKey] = useState("");
  const [walletMsg, setWalletMsg] = useState("");

  const [created, setCreated] = useState<Policy | null>(null);
  const [err, setErr] = useState("");
  const [jsonDraft, setJsonDraft] = useState("");

  function setCap(a: ActionKey, patch: Partial<CapState>) {
    setCaps((prev) => ({ ...prev, [a]: { ...prev[a], ...patch } }));
  }
  function toggleNet(netId: string) {
    setNetworks((prev) => (prev.includes(netId) ? prev.filter((x) => x !== netId) : [...prev, netId]));
  }
  function addCustomNet() {
    const n = customNet.trim();
    if (n && !networks.includes(n)) setNetworks((prev) => [...prev, n]);
    setCustomNet("");
  }
  // ADDITIVE: merge a template's capabilities onto the current ones (don't reset others)
  function applyTemplate(t: QuickTemplate) {
    setPaused(false);
    setCaps((prev) => {
      const next: Caps = { ...prev };
      for (const a of ACTIONS) {
        const vals = t.caps[a];
        if (vals) next[a] = { ...next[a], enabled: true, ...vals };
      }
      return next;
    });
  }

  // build the exact policy object the form will send
  const policy = useMemo(() => {
    const subjects: Record<string, unknown>[] = [{ id: subjectId.trim(), type: "operator" }];
    if (approverId.trim() && approverKey.trim()) {
      subjects.push({ id: approverId.trim(), type: "member", publicKey: { scheme: "secp256k1", key: approverKey.trim() } });
    }

    const rules: Record<string, unknown>[] = [];
    if (paused) {
      rules.push({ id: "deny-all", effect: "deny", actions: [...ACTIONS], chains: networks, subjects: [subjectId.trim()], constraints: {} });
    } else {
      for (const a of ACTIONS) {
        if (!caps[a].enabled) continue;
        rules.push({
          id: `allow-${a.replace(/\./g, "-")}`,
          effect: "allow",
          actions: [a],
          chains: networks,
          subjects: [subjectId.trim()],
          constraints: constraintsFor(a, caps[a]),
        });
      }
    }
    return { id: id.trim(), org: org.trim(), subjects, rules };
  }, [id, org, subjectId, networks, paused, caps, approverId, approverKey]);

  // plain-language "what this key can / cannot do"
  const summary = useMemo(() => {
    const yes: string[] = [];
    const no: string[] = [];
    const warn: string[] = [];
    if (!networks.length) warn.push("No network selected - pick at least one above, or the rules apply to no chain.");

    if (paused) {
      no.push(`Do ANYTHING - this policy denies every action for “${subjectId.trim() || "this key"}”.`);
      return { yes, no, warn };
    }
    for (const a of ACTIONS) {
      const c = caps[a];
      if (!c.enabled) continue;
      const m = ACTION_META[a];
      let line = m.verb;
      if (m.hasAmount) {
        const ua = atomToUatom(c.maxAtom);
        if (ua) line += `, up to ${c.maxAtom} ATOM per tx`;
        else {
          line += `, with NO amount cap`;
          warn.push(`“${m.label}” has no amount limit.`);
        }
        const r = list(c.targets);
        if (r.length) line += `, to ${r.length} allowed ${m.targetLabel?.toLowerCase().includes("validator") ? "validator" : "address"}${r.length > 1 ? "es" : ""}`;
        else {
          line += `, to ANY destination`;
          warn.push(`“${m.label}” has no allow-list.`);
        }
        const wua = atomToUatom(c.windowAtom);
        if (wua && Number(c.windowHours) > 0) line += `, max ${c.windowAtom} ATOM total per ${c.windowHours}h`;
      }
      if (m.isVote) line += c.proposals.trim() && c.proposals.trim() !== "*" ? `, on proposals ${list(c.proposals).join(", ")}` : `, on any proposal`;
      const reqN = Number(c.requireApprovals) || 0;
      if (reqN > 0) line += ` - after ${reqN} approval${reqN > 1 ? "s" : ""}`;
      if (c.hourStart.trim() && c.hourEnd.trim()) line += `, only ${c.hourStart}:00-${c.hourEnd}:00 UTC`;
      yes.push(line.charAt(0).toUpperCase() + line.slice(1) + ".");
    }
    if (!yes.length) no.push("Nothing yet - enable a function below (or pick a template).");
    else no.push("Anything not listed above - everything else is denied by default.");
    return { yes, no, warn };
  }, [networks, paused, caps, subjectId]);

  async function create(body: unknown) {
    setErr("");
    setCreated(null);
    try {
      setCreated(await api.createPolicy(body));
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }

  async function fillApproverFromKeplr() {
    setWalletMsg("");
    try {
      const { pubkeyB64, address } = await keplrGetPubkey();
      setApproverKey(pubkeyB64);
      if (!approverId.trim()) setApproverId("member:" + address.slice(-6));
      setWalletMsg(`Wallet ${address.slice(0, 14)}… - pubkey filled.`);
    } catch (e) {
      setWalletMsg((e as Error).message);
    }
  }

  const validation = useMemo(() => analyzePolicyJson(advJson), [advJson]);

  function goAdvanced() {
    setMode("advanced");
    if (!advJson.trim()) setAdvJson(JSON.stringify(policy, null, 2));
  }
  async function loadForEdit() {
    setEditLoadMsg("");
    setErr("");
    try {
      const p = await api.getPolicy(editLoadId.trim());
      setAdvJson(JSON.stringify(p, null, 2));
      setMode("advanced");
      setEditLoadMsg(`Loaded ${p.id} v${p.version} - edit, then Create to save a new version.`);
    } catch (e) {
      setEditLoadMsg(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }

  // --- lookup ---
  const [lookupId, setLookupId] = useState("");
  const [found, setFound] = useState<Policy | null>(null);
  const [lookupErr, setLookupErr] = useState("");
  async function lookup() {
    setLookupErr("");
    setFound(null);
    try {
      setFound(await api.getPolicy(lookupId.trim()));
    } catch (e) {
      setLookupErr(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }

  const enabledCount = paused ? 0 : ACTIONS.filter((a) => caps[a].enabled).length;

  return (
    <>
      <h2>Policies</h2>

      <div className="card">
        <h3>Build a policy</h3>
        <p className="muted small">
          Pick the networks, then turn on the functions this key should have. Each function you enable adds a rule - combine as many as you like in one policy. Watch the live code on the right.
        </p>

        <div className="seg" style={{ maxWidth: 380 }}>
          <button type="button" className={mode === "guided" ? "on" : ""} onClick={() => setMode("guided")}>
            Guided (composer)
          </button>
          <button type="button" className={mode === "advanced" ? "on" : ""} onClick={goAdvanced}>
            Write your own
          </button>
        </div>

        {mode === "guided" && (
          <>
        {/* identity */}
        <div className="row">
          <label>
            Policy name (id)
            <input value={id} onChange={(e) => setId(e.target.value)} />
          </label>
          <label>
            Team / org
            <input value={org} onChange={(e) => setOrg(e.target.value)} />
          </label>
          <label>
            Which key (operator)
            <input value={subjectId} onChange={(e) => setSubjectId(e.target.value)} placeholder="svc:bot" />
          </label>
        </div>

        {/* networks */}
        <h3 style={{ marginTop: 16 }}>Networks (chains Praxis acts on)</h3>
        <p className="muted small">All major Cosmos chains - and any custom chain id. Click to toggle.</p>
        <div className="chips">
          {NETWORKS.map((n) => (
            <button key={n.id} type="button" className={`chip${networks.includes(n.id) ? " on" : ""}`} onClick={() => toggleNet(n.id)} title={n.id}>
              <ChainIcon net={n} />
              {n.label}
            </button>
          ))}
        </div>
        <div className="row">
          <label style={{ margin: 0 }}>
            Add a custom chain id
            <input value={customNet} onChange={(e) => setCustomNet(e.target.value)} placeholder="e.g. axelar-dojo-1" onKeyDown={(e) => e.key === "Enter" && addCustomNet()} />
          </label>
          <button type="button" className="ghost" onClick={addCustomNet}>
            Add
          </button>
        </div>
        <p className="hint">Selected: {networks.length ? networks.join(", ") : "none"} - these become each rule’s “chains”.</p>

        <div className="builder">
          <div className="builder-form">
            {/* templates (additive) */}
            <h3 style={{ marginTop: 12 }}>Quick start (adds functions - combine freely)</h3>
            <div className="tpl-grid">
              {TEMPLATES.map((t) => (
                <button key={t.id} type="button" className="tpl" onClick={() => applyTemplate(t)}>
                  <div className="ico">{t.icon}</div>
                  <div className="nm">{t.name}</div>
                  <div className="bl">{t.blurb}</div>
                </button>
              ))}
            </div>

            {/* capabilities */}
            <h3 style={{ marginTop: 16 }}>Functions ({enabledCount} on)</h3>
            <p className="muted small">Toggle any combination. Each one on = one rule in the policy.</p>
            {ACTIONS.map((a) => {
              const m = ACTION_META[a];
              const c = caps[a];
              return (
                <div key={a} className={`cap${c.enabled ? " on" : ""}`}>
                  <div className="cap-head" onClick={() => setCap(a, { enabled: !c.enabled })}>
                    <span className="ic">{m.icon}</span>
                    <span>
                      <span className="nm">{m.label}</span>
                      <br />
                      <span className="bl">{m.verb}</span>
                    </span>
                    <span className="sw">{c.enabled ? "ON ✓" : "OFF"}</span>
                  </div>
                  {c.enabled && !paused && (
                    <div className="cap-body">
                      {m.hasAmount && (
                        <>
                          <div className="row">
                            <label>
                              Max per transaction (ATOM)
                              <input value={c.maxAtom} onChange={(e) => setCap(a, { maxAtom: e.target.value })} placeholder="0.1" inputMode="decimal" />
                            </label>
                            <label>
                              {m.targetLabel} (comma)
                              <input value={c.targets} onChange={(e) => setCap(a, { targets: e.target.value })} placeholder="cosmos1…, cosmos1…" />
                            </label>
                          </div>
                          <p className="hint">{atomToUatom(c.maxAtom) ? `= ${atomToUatom(c.maxAtom)} uatom` : "blank = no cap"}</p>
                        </>
                      )}
                      {m.isVote && (
                        <label>
                          Allowed proposals (numbers, or *)
                          <input value={c.proposals} onChange={(e) => setCap(a, { proposals: e.target.value })} placeholder="42, 77   (or *)" />
                        </label>
                      )}
                      <label>
                        Require approvals (0 = run immediately)
                        <input value={c.requireApprovals} onChange={(e) => setCap(a, { requireApprovals: e.target.value })} type="number" min={0} />
                      </label>

                      <details>
                        <summary className="muted small">Advanced limits</summary>
                        {m.hasAmount && (
                          <>
                            <div className="row">
                              <label>
                                Rolling cap (ATOM)
                                <input value={c.windowAtom} onChange={(e) => setCap(a, { windowAtom: e.target.value })} placeholder="5" inputMode="decimal" />
                              </label>
                              <label>
                                Over hours
                                <input value={c.windowHours} onChange={(e) => setCap(a, { windowHours: e.target.value })} placeholder="24" type="number" min={1} />
                              </label>
                            </div>
                            <p className="hint">Max total across a rolling window (summed from the audit log). 24 = per day · 1 = per hour · 168 = per week. The per-transaction cap is the field above.</p>
                          </>
                        )}
                        <div className="row">
                          <label>
                            Active from hour UTC
                            <input value={c.hourStart} onChange={(e) => setCap(a, { hourStart: e.target.value })} placeholder="8" type="number" min={0} max={24} />
                          </label>
                          <label>
                            to hour UTC
                            <input value={c.hourEnd} onChange={(e) => setCap(a, { hourEnd: e.target.value })} placeholder="18" type="number" min={0} max={24} />
                          </label>
                        </div>
                        <div className="row">
                          <label>
                            Not before (RFC3339)
                            <input value={c.notBefore} onChange={(e) => setCap(a, { notBefore: e.target.value })} placeholder="2026-01-01T00:00:00Z" />
                          </label>
                          <label>
                            Not after
                            <input value={c.notAfter} onChange={(e) => setCap(a, { notAfter: e.target.value })} placeholder="2026-12-31T23:59:59Z" />
                          </label>
                        </div>
                        <div className="row">
                          <label>
                            x/group chain id
                            <input value={c.groupChain} onChange={(e) => setCap(a, { groupChain: e.target.value })} placeholder="provider" />
                          </label>
                          <label>
                            x/group id
                            <input value={c.groupId} onChange={(e) => setCap(a, { groupId: e.target.value })} placeholder="1" />
                          </label>
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              );
            })}

            {/* pause / approver */}
            <label style={{ marginTop: 8 }}>
              <input type="checkbox" style={{ width: "auto", marginRight: 8 }} checked={paused} onChange={(e) => setPaused(e.target.checked)} />
              ⏸ Pause this key - deny everything (overrides all functions above)
            </label>

            <details style={{ marginTop: 8 }}>
              <summary className="muted small">Add a human approver (Keplr) for the “require approvals” functions</summary>
              <div className="row">
                <label>
                  Approver name (id)
                  <input value={approverId} onChange={(e) => setApproverId(e.target.value)} placeholder="member:alice" />
                </label>
                <button type="button" className="ghost" onClick={() => void fillApproverFromKeplr()}>
                  Use my Keplr key
                </button>
              </div>
              <label>
                Approver public key (base64, secp256k1)
                <input value={approverKey} onChange={(e) => setApproverKey(e.target.value)} placeholder="filled by Keplr" />
              </label>
              {walletMsg && <p className="muted small">{walletMsg}</p>}
            </details>

            {/* summary + create */}
            <div className="summary" style={{ marginTop: 14 }}>
              <ul>
                {summary.yes.map((s, i) => (
                  <li key={`y${i}`} className="yes">
                    {s}
                  </li>
                ))}
                {summary.no.map((s, i) => (
                  <li key={`n${i}`} className="no">
                    {s}
                  </li>
                ))}
              </ul>
              {summary.warn.map((w, i) => (
                <p key={`w${i}`} className="warn-text">
                  ⚠️ {w}
                </p>
              ))}
            </div>

            {err && <div className="err">{err}</div>}
            <button onClick={() => void create(policy)}>Create policy</button>

            {created && (
              <>
                <p className="badge ok" style={{ marginTop: 12 }}>
                  Created {created.id} v{created.version}
                </p>
                <pre>{JSON.stringify(created, null, 2)}</pre>
              </>
            )}

            <details style={{ marginTop: 12 }}>
              <summary className="muted small">Advanced - edit raw JSON</summary>
              <p className="muted small">Paste a full policy to override the form (custom denoms, extra rules, etc.).</p>
              <textarea value={jsonDraft} onChange={(e) => setJsonDraft(e.target.value)} placeholder="paste a full policy JSON…" />
              <button
                className="ghost"
                onClick={() => {
                  try {
                    void create(JSON.parse(jsonDraft));
                  } catch {
                    setErr("Invalid JSON");
                  }
                }}
              >
                Create from JSON
              </button>
            </details>
          </div>

          <aside className="builder-code">
            <div className="code-head">
              <span>
                📝 Live policy <span className="muted small">- {policy.rules.length} rule{policy.rules.length === 1 ? "" : "s"}</span>
              </span>
              <button type="button" className="ghost" onClick={() => void navigator.clipboard?.writeText(JSON.stringify(policy, null, 2))}>
                Copy
              </button>
            </div>
            <pre>{JSON.stringify(policy, null, 2)}</pre>
            <p className="muted small">This exact JSON is what “Create policy” sends to the API.</p>
          </aside>
        </div>
          </>
        )}

        {mode === "advanced" && (
          <>
            <div className="row" style={{ marginTop: 12 }}>
              <label style={{ margin: 0 }}>
                Load existing policy by id (to edit → saves a new version)
                <input value={editLoadId} onChange={(e) => setEditLoadId(e.target.value)} placeholder="p1" onKeyDown={(e) => e.key === "Enter" && void loadForEdit()} />
              </label>
              <button type="button" className="ghost" onClick={() => void loadForEdit()}>
                Load
              </button>
              <button type="button" className="ghost" onClick={() => setAdvJson(JSON.stringify(ADVANCED_EXAMPLE, null, 2))}>
                Load advanced example
              </button>
            </div>
            {editLoadMsg && <p className="muted small">{editLoadMsg}</p>}

            <div className="builder">
              <div className="builder-form">
                <label style={{ margin: "8px 0 0" }}>
                  Policy JSON
                  <textarea className="code-editor" value={advJson} onChange={(e) => setAdvJson(e.target.value)} spellCheck={false} placeholder="write or paste a full policy JSON…" />
                </label>
                {err && <div className="err">{err}</div>}
                <button disabled={validation.errors.length > 0 || !validation.parsed} onClick={() => validation.parsed && void create(validation.parsed)}>
                  Create policy
                </button>
                {created && (
                  <p className="badge ok" style={{ marginLeft: 10 }}>
                    Created {created.id} v{created.version}
                  </p>
                )}
              </div>

              <aside className="builder-code">
                <div className="code-head">
                  <span>🔎 Live validation</span>
                </div>
                {advJson.trim() && validation.errors.length === 0 && validation.warnings.length === 0 && <p className="badge ok">✓ Looks valid - safe to create</p>}
                {validation.errors.map((e, i) => (
                  <p key={`e${i}`} className="err" style={{ margin: "6px 0" }}>
                    ❌ {e}
                  </p>
                ))}
                {validation.warnings.map((w, i) => (
                  <p key={`w${i}`} className="warn-text">
                    ⚠️ {w}
                  </p>
                ))}
                <details style={{ marginTop: 10 }} open>
                  <summary className="muted small">Rule schema cheatsheet</summary>
                  <pre>{SCHEMA_CHEATSHEET}</pre>
                </details>
              </aside>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h3>Look up a policy</h3>
        <div className="row">
          <label style={{ margin: 0 }}>
            Policy id
            <input value={lookupId} onChange={(e) => setLookupId(e.target.value)} placeholder="p1" />
          </label>
          <button onClick={() => void lookup()}>Fetch</button>
        </div>
        {lookupErr && <div className="err">{lookupErr}</div>}
        {found && <pre>{JSON.stringify(found, null, 2)}</pre>}
      </div>
    </>
  );
}
